import React from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import Button from '../components/Button';
import { getFeaturedProducts } from '../services/products';
import { useI18n } from '../context/I18nContext';

const HomePage = () => {
  const featured = getFeaturedProducts();
  const { t, translateCategory } = useI18n();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-800 to-amber-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 text-9xl">🐾</div>
          <div className="absolute bottom-10 right-10 text-9xl">🐾</div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-9xl opacity-20">🐾</div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-center">
          <p className="text-amber-300 font-medium tracking-widest text-sm uppercase mb-4">{t('home.eyebrow')}</p>
          <h1 className="text-4xl md:text-6xl font-bold font-serif mb-6 leading-tight">
            Paws Eternal
          </h1>
          <p className="text-xl md:text-2xl text-amber-100 mb-4 font-serif italic">
            {t('home.tagline')}
          </p>
          <p className="text-amber-200 max-w-2xl mx-auto mb-10 text-base md:text-lg leading-relaxed">
            {t('home.heroDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products">
              <Button className="text-lg px-8 py-4">{t('home.shopCollection')}</Button>
            </Link>
            <Link to="/products?category=Wild Animals">
              <Button variant="outline" className="text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-amber-900">
                {t('home.wildlifeSeries')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Banner */}
      <section className="bg-amber-50 border-y border-amber-100">
        <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { icon: '🎨', text: t('home.features.handcrafted') },
            { icon: '🚚', text: t('home.features.freeShipping') },
            { icon: '💎', text: t('home.features.quality') },
            { icon: '↩️', text: t('home.features.returns') },
          ].map(item => (
            <div key={item.text} className="flex flex-col items-center gap-2">
              <span className="text-2xl">{item.icon}</span>
              <span className="text-sm font-medium text-amber-800">{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold font-serif text-amber-900 mb-3">
            {t('home.featuredTitle')}
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto">
            {t('home.featuredDescription')}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {featured.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
        <div className="text-center">
          <Link to="/products">
            <Button variant="outline">{t('home.viewAll')}</Button>
          </Link>
        </div>
      </section>

      {/* Categories Section */}
      <section className="bg-amber-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold font-serif text-amber-900 mb-3">{t('home.shopByCategory')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: translateCategory('Dog'), emoji: '🐕', desc: t('home.categories.dogsDesc'), category: 'Dog' },
              { name: translateCategory('Cat'), emoji: '🐈', desc: t('home.categories.catsDesc'), category: 'Cat' },
              { name: translateCategory('Wild Animals'), emoji: '🦁', desc: t('home.categories.wildlifeDesc'), category: 'Wild Animals' },
            ].map(cat => (
              <Link key={cat.name} to={`/products?category=${encodeURIComponent(cat.category)}`}>
                <div className="bg-white rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition-all duration-300 border border-amber-100 group cursor-pointer">
                  <span className="text-5xl mb-4 block group-hover:scale-110 transition-transform duration-200">{cat.emoji}</span>
                  <h3 className="text-xl font-bold text-amber-900 mb-2">{cat.name}</h3>
                  <p className="text-gray-500 text-sm">{cat.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 py-16 text-center">
        <div className="bg-gradient-to-r from-amber-700 to-amber-800 rounded-3xl p-12 text-white">
          <h2 className="text-3xl md:text-4xl font-bold font-serif mb-4">
            {t('home.ctaTitle')}
          </h2>
          <p className="text-amber-100 mb-8 max-w-xl mx-auto">
            {t('home.ctaDescription')}
          </p>
          <Link to="/products">
            <button className="bg-white text-amber-800 font-bold px-8 py-4 rounded-lg hover:bg-amber-50 transition-colors text-lg">
              {t('home.ctaButton')}
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
